<template>
    <div>
        <button type="button"
            class="w-full py-3 text-center font-normal text-sm tracking-relaxed cursor-pointer select-none uppercase transition-colors rounded-full"
            :class="reversed ? 'text-black bg-white hover:bg-blue-200  hover:text-blue-600' : 'text-white bg-blue-600 hover:bg-blue-500'"
            v-html="button_text"
            @click="showModal"
        ></button>

        <modal
            :name="modal_name"
            :adaptive="true"
            :focusTrap="true"
            :width="600"
            height="auto"
            :styles="{ boxShadow: 'none', background: 'transparent' }"
            overlayTransition="overlay"
            @before-close="beforeClose"
        >
            <div class="rounded-lg bg-white px-6 sm:px-12 py-6 sm:py-10">
                <h2 class="text-center text-xl xs:text-2xl text-blue-700">
                    {{ $t('Get Started with Contract2Close') }}
                </h2>

                <p class="text-center text-base font-light uppercase tracking-widest text-gray-500 pt-1.5">
                    {{ plan_title }}
                </p>

                <div class="mx-auto w-24 h-1 bg-blue-600 my-4"></div>

                <div v-if="has_unadvertised_discount && showing_unadvertised_discount">
                    <p class="text-center text-lg pt-6 mb-0">
                        <span class="font-semibold text-green-warm-600">Good news!</span> As a member of {{ tenant.stripe_customer_description }}, you are entitled to an additional discount:
                    </p>

                    <div class="border-y border-blue-200 py-4 my-6">
                        <div class="w-full flex items-center justify-center text-center font-light text-gray-400 text-2xl leading-normal pt-2">
                            <div class="relative px-1">
                                <span class="font-light pr-1">{{ plan.currency_symbol }}</span>{{ plan.price_advertised_display }}<span v-if="plan.is_monthly" class="uppercase text-sm tracking-wide"><span class="px-1">/</span>Month</span><span v-if="plan.is_yearly" class="uppercase text-sm tracking-wide"><span class="px-1">/</span>Year</span>
                                <div class="absolute h-0.5 bg-blue-600 w-full left-0 right-0 top-5 -rotate-12"></div>
                            </div>
                        </div>

                        <div class="w-full text-center text-black text-4xl">
                            <span class="font-light pr-1">{{ plan.currency_symbol }}</span>{{ plan.price_display }}<span v-if="plan.is_monthly" class="uppercase text-sm tracking-wide"><span class="px-1">/</span>Month</span><span v-if="plan.is_yearly" class="uppercase text-sm tracking-wide"><span class="px-1">/</span>Year</span>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <p v-if="plan.trial_days"
                        class="text-center text-gray-600 text-base sm:text-md mb-0 py-1 leading-relaxed"
                    >
                        <span class="text-lg font-medium text-blue-700">{{ $t('Free trial for') }} {{ plan.trial_days }} {{ $t('days') }} ...</span>
                        <br>{{ $t('No credit card required to sign up!') }}
                    </p>

                    <div class="flex flex-col mt-2 mb-3 xs:mb-6">
                        <div class="h-4 flex items-center justify-between leading-none mb-3">
                            <label class="uppercase text-gray-700 text-xs tracking-wider leading-none">
                                {{ $t('Name') }}
                            </label>
                        </div>

                        <input type="name"
                            class="flex-1 field text-base xs:text-lg"
                            name="name"
                            v-model="name"
                            @keyup.enter="submit"
                        >
                    </div>

                    <div class="flex flex-col mb-3 xs:mb-6">
                        <div class="h-4 flex items-center justify-between leading-none mb-3">
                            <label class="uppercase text-gray-700 text-xs tracking-wider leading-none">
                                {{ $t('signup.email') }}
                            </label>
                        </div>

                        <input type="email"
                            class="flex-1 field text-base xs:text-lg"
                            name="email"
                            v-model="email"
                            @keyup.enter="submit"
                        >
                    </div>

                    <div class="flex flex-col mb-3 xs:mb-6">
                        <div class="h-4 flex items-center justify-between leading-none mb-3">
                            <label class="uppercase text-gray-700 text-xs tracking-wider">
                                {{ $t('signup.password') }}
                            </label>
                        </div>

                        <input type="password"
                            class="flex-1 field text-base xs:text-lg"
                            name="password"
                            required
                            v-model="password"
                            @keyup.enter="submit"
                        >
                    </div>

                    <div v-if="! tenant || tenant.allow_promo_codes"
                        class="flex flex-col mb-3 xs:mb-6"
                    >
                        <div class="h-4 flex items-center justify-between leading-none mb-3">
                            <label class="uppercase text-gray-700 text-xs tracking-wider">
                                Promo Code
                            </label>
                        </div>

                        <input type="text"
                            class="flex-1 field text-base xs:text-lg"
                            name="promo_code"
                            v-model="promo_code"
                            @keyup.enter="submit"
                        >
                    </div>

                    <div class="mb-0">
                        <input-checkbox
                            name="terms_of_use"
                            xlabel='<span class="text-sm font-medium">I agree to the <a href="/user-agreement" target="_blank" class="text-blue-500 hover:text-blue-700 focus:outline-none focus:shadow-none select-none">terms of use</a> for Contract2Close</span>'
                            v-model="terms_of_use"
                            size="small"
                        >
                            <span class="text-sm font-medium">
                                {{ $t('I agree to the') }}
                                <a href="/user-agreement" target="_blank" class="text-blue-500 hover:text-blue-700 focus:outline-none focus:shadow-none select-none">{{ $t('terms of use') }}</a>
                                {{ $t('for Contract2Close') }}
                            </span>
                        </input-checkbox>
                    </div>

                    <form-errors v-if="errors.length" class="py-2" :errors="errors" />
                </div>

                <div class="flex items-center justify-between pt-4">
                    <div>
                        <transition name="fade-in">
                            <div v-if="submitting"
                                class="flex items-center text-blue-500"
                            >
                                <spinner class="w-6 h-6" />

                                <p class="leading-none pl-3">
                                    {{ $t('Signing up...') }}
                                </p>
                            </div>
                        </transition>
                    </div>

                    <button type="button"
                        class="flex-none uppercase tracking-widest text-white text-sm bg-green-warm-600 hover:text-white px-12 py-4 rounded-full"
                        :class="submitting
                            ? 'opacity-50 hover:bg-green-warm-600 cursor-not-allowed'
                            : 'opacity-100 hover:bg-green-warm-500 cursor-pointer'
                        "
                        @click="submit"
                    >
                        <span v-if="showing_unadvertised_discount">
                            Accept Discount
                        </span>

                        <span v-else>
                            {{ $t('signup.signup_button') }}
                        </span>
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import FormErrors from './form-errors.vue';
import Spinner from './spinner.vue';

export default {
    components: {
        FormErrors,
        Spinner,
    },

    props: {
        plan: {},
        tenant: {},
        reversed: { default: false },
        button_text: { default: 'Get Started' },
    },

    data() {
        return {
            name: null,
            email: null,
            password: null,
            terms_of_use: false,
            errors: [],
            submitting: false,
            promo_code: null,
            showing_unadvertised_discount: false,
        }
    },

    computed: {
        modal_name() {
            return 'signup_plan_' + this.plan.id;
        },
        plan_title() {
            return this.plan.title || this.plan.name;
        },
        has_unadvertised_discount() {
            return this.tenant && this.plan.price_advertised;
        },
    },

    methods: {
        showModal() {
            this.$modal.show(this.modal_name);
        },
        beforeClose(event) {
            this.clearFields();
            this.$emit('closed');
        },
        submit() {
            if (this.has_unadvertised_discount && ! this.showing_unadvertised_discount) {
                this.showing_unadvertised_discount = true;
                return;
            }

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            axios.post('/auth/register', {
                name: this.name,
                email: this.email,
                password: this.password,
                terms_of_use: this.terms_of_use,
                plan_id: this.plan.id,
                promo_code: this.promo_code,
            }).then(response => {
                window.location.replace(response.data.redirect);
            }).catch(error => {
                this.submitting = false;

                if (error.response.data.errors) {
                    this.errors = Object.values(error.response.data.errors);
                }
            });
        },
        clearFields() {
            this.name = null;
            this.email = null;
            this.password = null;
            this.terms_of_use = false;
            this.errors = [];
            this.submitting = false;
        },
    },
}
</script>

<style scoped>
.vm--overlay {
    background: #000000;
    opacity: 0.7;
}

.overlay-enter,
.overlay-leave-to {
    opacity: 0;
}

.overlay-enter-active,
.overlay-leave-active {
    transition: all .3s;
}

.fade-in-enter-active, .fade-in-leave-active {
  transition: opacity .3s;
}

.fade-in-enter, .fade-in-leave-to {
  opacity: 0;
}
</style>
